import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useRessource } from '../hooks/useRessource';
import { ErreurApi, api } from '../lib/apiClient';
import { Badge, Infobulle } from './Elements';
import type { EtatCompteGoogle, SyntheseDocuments } from '../types';

/**
 * L'état de l'archivage pour UNE école (fiche de l'entité) : le compte
 * technique est-il connecté, le Drive répond-il, combien de documents
 * attendent. L'identifiant du Drive, lui, est un champ du formulaire de
 * la fiche — c'est lui qu'on enregistre avec le reste.
 */
export function ArchivageEntite({
  entiteId,
  driveIdEnregistre,
  peutTester,
}: {
  entiteId: string;
  driveIdEnregistre: string | null;
  peutTester: boolean;
}) {
  const compte = useRessource(() => api.get<EtatCompteGoogle>('/api/admin/google/drive'), []);
  const synthese = useRessource(() => api.get<SyntheseDocuments>('/api/documents/synthese'), [entiteId]);
  const [resultat, setResultat] = useState<{ ok: boolean; texte: string } | null>(null);
  const [enCours, setEnCours] = useState(false);

  const tester = async () => {
    setEnCours(true);
    setResultat(null);
    try {
      const r = await api.post<{ ok: boolean; fichiers?: number; erreur?: string }>(
        `/api/admin/entites/${entiteId}/drive/test`,
        {},
      );
      setResultat(
        r.ok
          ? {
              ok: true,
              texte: `Le Drive répond et le compte en est membre — ${r.fichiers ?? 0} fichier${(r.fichiers ?? 0) > 1 ? 's' : ''} déposé${(r.fichiers ?? 0) > 1 ? 's' : ''} par l’application.`,
            }
          : { ok: false, texte: r.erreur ?? 'Le test a échoué.' },
      );
      compte.recharger();
    } catch (cause) {
      setResultat({ ok: false, texte: cause instanceof ErreurApi ? cause.message : 'Test impossible.' });
    } finally {
      setEnCours(false);
    }
  };

  const connexion = compte.donnees?.connexion ?? null;

  return (
    <div className="space-y-2 text-sm">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        {connexion ? (
          <>
            <Badge ton="succes">compte connecté</Badge>
            <span className="text-xs text-encre-500">{connexion.compte}</span>
          </>
        ) : (
          <>
            <Badge ton="neutre">aucun compte connecté</Badge>
            <Link to="/admin/parametres" className="text-xs underline">
              Administration → Paramètres
            </Link>
          </>
        )}
        {synthese.donnees && (
          <span className="text-xs text-encre-500">
            · {synthese.donnees.archives} archivé{synthese.donnees.archives > 1 ? 's' : ''},{' '}
            {synthese.donnees.en_attente} en attente
          </span>
        )}
        {peutTester && (
          <Infobulle
            texte={
              !driveIdEnregistre
                ? 'Renseignez et enregistrez d’abord l’identifiant du Drive.'
                : !connexion
                  ? 'Connectez d’abord le compte technique.'
                  : null
            }
            className="ml-auto inline-flex"
          >
            {/* `disabled:pointer-events-none` : un bouton inactif n'emet
                pas de survol, c'est l'enveloppe qui le recoit. */}
            <button
              type="button"
              className="bouton-secondaire py-1 text-xs disabled:pointer-events-none"
              disabled={enCours || !connexion || !driveIdEnregistre}
              onClick={() => void tester()}
            >
              {enCours ? 'Test…' : 'Tester la connexion'}
            </button>
          </Infobulle>
        )}
      </div>
      {resultat && (
        <p
          className={`rounded-md border p-2 text-xs ${
            resultat.ok ? 'border-emerald-200 bg-emerald-50 text-emerald-900' : 'border-red-200 bg-red-50 text-red-800'
          }`}
          role="status"
        >
          {resultat.texte}
        </p>
      )}
    </div>
  );
}
